import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { pagesService, pageVersionsService, auditService } from '../../lib/services';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

// ─── helpers ────────────────────────────────────────────────
const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase()),
      toCamel(v),
    ])
  );
};

const fmt = (ts) =>
  ts
    ? new Date(ts)?.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const actionColor = (action) => {
  const map = {
    create: 'bg-emerald-100 text-emerald-700',
    update: 'bg-blue-100 text-blue-700',
    delete: 'bg-red-100 text-red-700',
    publish: 'bg-purple-100 text-purple-700',
    rollback: 'bg-amber-100 text-amber-700',
  };
  return map?.[action?.toLowerCase()] || 'bg-muted text-muted-foreground';
};

// ─── sub-components ─────────────────────────────────────────
const EmptyState = ({ icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
      <Icon name={icon} size={24} className="text-muted-foreground" />
    </div>
    <p className="text-base font-medium text-foreground mb-1">{title}</p>
    <p className="text-sm text-muted-foreground max-w-xs">{description}</p>
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// ─── VersionHistoryTab ───────────────────────────────────────
const VersionHistoryTab = ({ pages }) => {
  const [selectedPage, setSelectedPage] = useState('');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rolling, setRolling] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadVersions = useCallback(async (pageId) => {
    setLoading(true);
    const { data } = await pageVersionsService?.getByPageId(pageId);
    setVersions(data || []);
    setLoading(false);
  }, []);

  const handlePageChange = (e) => {
    const id = e?.target?.value;
    setSelectedPage(id);
    setVersions([]);
    if (id) loadVersions(id);
  };

  const handleRollback = async (version) => {
    if (!window.confirm(`Restore page to version ${version?.versionNumber}? This will overwrite the current content.`)) return;
    setRolling(version?.id);
    try {
      const { error } = await supabase?.from('pages')?.update({ title: version?.title, content: version?.content })?.eq('id', selectedPage);
      if (error) throw error;

      // log rollback
      await auditService?.log('rollback', 'page', selectedPage, null, {
        restored_to_version: version?.versionNumber,
        title: version?.title,
      });

      showToast(`Rolled back to version ${version?.versionNumber} successfully`);
      loadVersions(selectedPage);
    } catch (err) {
      showToast(err?.message || 'Rollback failed', 'error');
    } finally {
      setRolling(null);
    }
  };

  const selectedPageObj = pages?.find((p) => p?.id === selectedPage);

  return (
    <div className="space-y-6">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 ${
            toast?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-emerald-600 text-white'
          }`}
        >
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} />
          {toast?.msg}
        </div>
      )}
      {/* Page selector */}
      <div className="bg-card border border-border rounded-xl p-5">
        <label className="block text-sm font-medium text-foreground mb-2">Select a Page</label>
        <select
          value={selectedPage}
          onChange={handlePageChange}
          className="w-full max-w-sm px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="">— choose a page —</option>
          {pages?.map((p) => (
            <option key={p?.id} value={p?.id}>
              {p?.title} ({p?.status})
            </option>
          ))}
        </select>
      </div>
      {/* Versions list */}
      {selectedPage && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Version History — {selectedPageObj?.title}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {versions?.length} version{versions?.length !== 1 ? 's' : ''} saved
              </p>
            </div>
            <Icon name="GitBranch" size={18} className="text-muted-foreground" />
          </div>

          {loading ? (
            <Spinner />
          ) : versions?.length === 0 ? (
            <EmptyState
              icon="GitCommit"
              title="No versions yet"
              description="Versions are saved automatically when content is edited."
            />
          ) : (
            <div className="divide-y divide-border">
              {versions?.map((v, idx) => (
                <div key={v?.id} className="flex items-start gap-4 px-5 py-4 hover:bg-muted/30 transition-colors">
                  {/* timeline dot */}
                  <div className="flex flex-col items-center mt-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        idx === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      v{v?.versionNumber}
                    </div>
                    {idx < versions?.length - 1 && (
                      <div className="w-px flex-1 bg-border mt-1 min-h-[20px]" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-sm font-medium text-foreground truncate">{v?.title || 'Untitled'}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {v?.creator?.fullName || 'Unknown'} · {fmt(v?.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {idx === 0 && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                            Current
                          </span>
                        )}
                        {idx > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="RotateCcw"
                            iconPosition="left"
                            disabled={rolling === v?.id}
                            onClick={() => handleRollback(v)}
                          >
                            {rolling === v?.id ? 'Restoring…' : 'Restore'}
                          </Button>
                        )}
                      </div>
                    </div>

                    {v?.content && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2 bg-muted/40 rounded px-2 py-1 font-mono">
                        {v?.content?.replace(/<[^>]+>/g, ' ')?.slice(0, 120)}…
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!selectedPage && (
        <EmptyState
          icon="GitBranch"
          title="Select a page to view its history"
          description="Choose a page from the dropdown above to browse saved versions and restore any of them."
        />
      )}
    </div>
  );
};

// ─── AuditLogTab ─────────────────────────────────────────────
const AuditLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entityType: '', action: '', limit: 100 });
  const [expanded, setExpanded] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const params = { limit: filters?.limit };
    if (filters?.entityType) params.entityType = filters?.entityType;
    const { data } = await auditService?.getAll(params);
    let filtered = data || [];
    if (filters?.action) {
      filtered = filtered?.filter((l) => l?.action?.toLowerCase() === filters?.action?.toLowerCase());
    }
    setLogs(filtered);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const entityTypes = [...new Set(logs?.map((l) => l?.entityType).filter(Boolean))];
  const actionTypes = [...new Set(logs?.map((l) => l?.action).filter(Boolean))];

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Entity Type</label>
            <select
              value={filters?.entityType}
              onChange={(e) => setFilters((f) => ({ ...f, entityType: e?.target?.value }))}
              className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All types</option>
              {entityTypes?.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Action</label>
            <select
              value={filters?.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e?.target?.value }))}
              className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All actions</option>
              {actionTypes?.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Limit</label>
            <select
              value={filters?.limit}
              onChange={(e) => setFilters((f) => ({ ...f, limit: Number(e?.target?.value) }))}
              className="px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {[50, 100, 250, 500]?.map((n) => (
                <option key={n} value={n}>{n} entries</option>
              ))}
            </select>
          </div>
          <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={loadLogs}>
            Refresh
          </Button>
        </div>
      </div>
      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Events', value: logs?.length, icon: 'Activity', color: 'text-primary' },
          { label: 'Creates', value: logs?.filter((l) => l?.action === 'create')?.length, icon: 'Plus', color: 'text-emerald-600' },
          { label: 'Updates', value: logs?.filter((l) => l?.action === 'update')?.length, icon: 'Pencil', color: 'text-blue-600' },
          { label: 'Deletes', value: logs?.filter((l) => l?.action === 'delete')?.length, icon: 'Trash2', color: 'text-red-600' },
        ]?.map((s) => (
          <div key={s?.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Icon name={s?.icon} size={18} className={s?.color} />
            </div>
            <div>
              <p className="text-xl font-bold text-foreground">{s?.value}</p>
              <p className="text-xs text-muted-foreground">{s?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Log table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Activity Log</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Showing {logs?.length} events</p>
        </div>

        {loading ? (
          <Spinner />
        ) : logs?.length === 0 ? (
          <EmptyState icon="ClipboardList" title="No activity yet" description="Admin actions will appear here as they happen." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Action</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Entity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs?.map((log) => (
                  <React.Fragment key={log?.id}>
                    <tr
                      className="hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => setExpanded(expanded === log?.id ? null : log?.id)}
                    >
                      <td className="px-5 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmt(log?.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                            {log?.user?.fullName?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="text-xs text-foreground truncate max-w-[120px]">
                            {log?.user?.fullName || log?.user?.email || 'System'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(log?.action)}`}>
                          {log?.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground font-medium">{log?.entityType}</span>
                        <p className="text-xs text-muted-foreground font-mono truncate max-w-[100px]">{log?.entityId?.slice(0, 8)}…</p>
                      </td>
                      <td className="px-4 py-3">
                        <button className="text-xs text-primary hover:underline flex items-center gap-1">
                          <Icon name={expanded === log?.id ? 'ChevronUp' : 'ChevronDown'} size={12} />
                          {expanded === log?.id ? 'Hide' : 'View'}
                        </button>
                      </td>
                    </tr>
                    {expanded === log?.id && (
                      <tr className="bg-muted/10">
                        <td colSpan={5} className="px-5 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {log?.oldValues && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">Before</p>
                                <pre className="text-xs bg-muted rounded-lg p-3 overflow-auto max-h-40 text-foreground">
                                  {JSON.stringify(log?.oldValues, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log?.newValues && (
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">After</p>
                                <pre className="text-xs bg-muted rounded-lg p-3 overflow-auto max-h-40 text-foreground">
                                  {JSON.stringify(log?.newValues, null, 2)}
                                </pre>
                              </div>
                            )}
                            {!log?.oldValues && !log?.newValues && (
                              <p className="text-xs text-muted-foreground">No payload recorded for this event.</p>
                            )}
                          </div>
                          {log?.ipAddress && (
                            <p className="text-xs text-muted-foreground mt-2">IP: {log?.ipAddress}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────
const TABS = [
  { id: 'versions', label: 'Version History', icon: 'GitBranch' },
  { id: 'audit', label: 'Audit Log', icon: 'ClipboardList' },
];

const VersionControlPage = () => {
  const [activeTab, setActiveTab] = useState('versions');
  const [pages, setPages] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    pagesService?.getAll()?.then(({ data }) => setPages(data || []));
  }, []);

  return (
    <div className="app-layout">
      <Sidebar isCollapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />
      <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header userName="Admin" userRole="Administrator" />
        <main className="page-content">
          {/* Page header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="GitBranch" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">Version Control</h1>
                <p className="text-sm text-muted-foreground">Browse page history, restore versions, and review admin activity</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-muted rounded-xl w-fit mb-6">
            {TABS?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab === 'versions' && <VersionHistoryTab pages={pages} />}
          {activeTab === 'audit' && <AuditLogTab />}
        </main>
      </div>
    </div>
  );
};

export default VersionControlPage;
